// src/components/tactic/HeroPickerPanel.jsx
// Hero shelf and scouting in one panel: pick the team, read every hero's record
// on its own tile, then click to drop it on the board — or to open its stats.
//
// The lane filter prefers what the team has ACTUALLY played (built from the
// uploaded workbook) and only falls back to a class guess for heroes with no
// picks on record — so the shelf reflects this team's reads, not theory.

import React, { useMemo, useState } from "react";
import heroList from "../../data/heroList.json";
import { getHeroImageUrlWithFallback } from "../../utils/heroCatalog";
import {
  LANES,
  hasLaneData,
  laneBreakdown,
  lanesForHero,
  pickCount,
} from "../../utils/tactic/heroLanes";
import { ALL_TEAMS_KEY } from "../../utils/tactic/teamScout";
import { Btn, Label, Panel, Row, T, TextInput } from "./tacticUi";

// heroList carries a row per role, so dual-role heroes (Fennik, Murad, …)
// appear twice. Collapse to one tile each, keeping the first role listed.
const HEROES = (() => {
  const rows = Array.isArray(heroList) ? heroList : heroList?.heroes || [];
  const seen = new Map();
  for (const h of rows) {
    if (h?.name && !seen.has(h.name)) seen.set(h.name, h);
  }
  return [...seen.values()];
})();

const ALL = "ทั้งหมด";
const FILTERS = [ALL, ...LANES];

export default function HeroPickerPanel({
  addTeam,
  onAddTeamChange,
  onPickHero,
  usedHeroes,
  laneIndex,
  // Scouting side of the panel
  teams = [],
  team = ALL_TEAMS_KEY,
  onTeamChange,
  record,
  statsOf,
  poolNote,
  onWantStats,
  onOpenSheet,
  onOpenHero,
}) {
  const [query, setQuery] = useState("");
  const [lane, setLane] = useState(ALL);
  // A click on a tile either places the hero or opens its numbers. Placing
  // stays the default — it is what the board is for.
  const [clickMode, setClickMode] = useState("place");

  const hasData = (laneIndex?.picks || 0) > 0;
  const scouting = typeof onOpenSheet === "function";
  const stats = (name, heroClass) =>
    typeof statsOf === "function" ? statsOf(name, heroClass) : null;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = HEROES.filter((h) => {
      if (!h?.name) return false;
      if (q && !h.name.toLowerCase().includes(q)) return false;
      if (lane !== ALL && !lanesForHero(laneIndex, h.name, h.role).includes(lane)) return false;
      return true;
    });

    // Inside a lane, the heroes this team leans on should come first.
    if (lane !== ALL && hasData) {
      return [...matched].sort(
        (a, b) => pickCount(laneIndex, b.name, lane) - pickCount(laneIndex, a.name, lane)
      );
    }
    return matched;
  }, [query, lane, laneIndex, hasData]);

  return (
    <Panel
      title={scouting ? "เลือกฮีโร่ · สถิติ" : "เลือกฮีโร่"}
      right={<span style={{ color: T.faint, fontSize: 11 }}>{results.length} ตัว</span>}
    >
      {scouting && (
        <>
          <Label>ทีมที่ดูสถิติ</Label>
          <select
            value={team}
            onChange={(e) => {
              onWantStats?.();
              onTeamChange(e.target.value);
            }}
            aria-label="เลือกทีมที่จะดูสถิติ"
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: "rgba(0,0,0,0.35)",
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              color: T.ink,
              fontFamily: T.font,
              fontSize: 13,
              padding: "8px 9px",
              marginBottom: 7,
            }}
          >
            <option value={ALL_TEAMS_KEY}>ทุกทีม</option>
            {teams.map((t) => (
              <option key={t.code} value={t.code}>
                {t.code} (
                {[t.games ? `${t.games} เกม` : "", t.scouted ? `ส่อง ${t.scouted}` : ""]
                  .filter(Boolean)
                  .join(" · ")}
                )
              </option>
            ))}
          </select>

          {record?.games > 0 && (
            <div style={{ fontSize: 12, color: T.dim, marginBottom: 8, lineHeight: 1.6 }}>
              {team === ALL_TEAMS_KEY ? "ทั้งหมด" : "เจอกัน"} {record.games} เกม ·{" "}
              <b style={{ color: T.green }}>{record.wins}</b>
              <span style={{ color: T.faint }}>-</span>
              <b style={{ color: T.red }}>{record.losses}</b>{" "}
              <b style={{ color: record.winRate >= 50 ? T.green : T.red }}>({record.winRate}%)</b>
            </div>
          )}

          <Btn
            tone="accent"
            onClick={() => onOpenSheet("played")}
            style={{ width: "100%", marginBottom: 10 }}
          >
            📊 เปิดหน้าต่างสถิติ
          </Btn>

          <Label>กดที่ฮีโร่เพื่อ</Label>
          <Row gap={6} style={{ marginBottom: 10 }}>
            <Btn
              tone="ghost"
              active={clickMode === "place"}
              onClick={() => setClickMode("place")}
              style={{ flex: 1 }}
            >
              วางลงแมพ
            </Btn>
            <Btn
              tone="ghost"
              active={clickMode === "stats"}
              onClick={() => {
                onWantStats?.();
                setClickMode("stats");
              }}
              style={{ flex: 1 }}
            >
              ดูสถิติ
            </Btn>
          </Row>
        </>
      )}

      <Label>วางลงทีม</Label>
      <Row style={{ marginBottom: 10 }}>
        <Btn tone="blue" active={addTeam === "blue"} onClick={() => onAddTeamChange("blue")} style={{ flex: 1 }}>
          ฝั่งบลู
        </Btn>
        <Btn tone="red" active={addTeam === "red"} onClick={() => onAddTeamChange("red")} style={{ flex: 1 }}>
          ฝั่งเรด
        </Btn>
      </Row>

      <TextInput
        placeholder="ค้นหาฮีโร่…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <Row gap={5} style={{ marginBottom: 6 }}>
        {FILTERS.map((f) => (
          <Btn
            key={f}
            tone="ghost"
            active={lane === f}
            onClick={() => setLane(f)}
            style={{ padding: "4px 8px", fontSize: 11.5 }}
          >
            {f}
          </Btn>
        ))}
        {/* Sits with the hero controls rather than up beside the team picker —
            it is about these heroes, and it was a panel away from them. */}
        {scouting && (
          <Btn
            onClick={() => onOpenSheet("all")}
            title="ฮีโร่ทั้งหมดในเกม เรียงตามตารางดราฟ พร้อมสถิติของทีมที่เลือก"
            style={{ padding: "4px 8px", fontSize: 11.5, marginLeft: "auto" }}
          >
            ⤢ ฮีโร่ทั้งหมด
          </Btn>
        )}
      </Row>

      <div style={{ fontSize: 10.5, color: T.faint, marginBottom: 9, lineHeight: 1.55 }}>
        {hasData
          ? `เลนจากสถิติจริง ${laneIndex.picks.toLocaleString()} พิก จาก ${laneIndex.games} เกม · เรียงตามที่เล่นบ่อย`
          : "ยังไม่มีไฟล์เกม — เลนเดาจากคลาสฮีโร่ อัปโหลด .xlsx เพื่อให้แม่นขึ้น"}
        {scouting && ` · เลขบนรูป = วินเรทของเราในทีมที่เลือก${poolNote ? ` (${poolNote})` : ""}`}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(62px, 1fr))",
          gap: 8,
          maxHeight: 420,
          overflowY: "auto",
          paddingRight: 3,
        }}
      >
        {results.map((h) => {
          const used = usedHeroes?.has(h.name);
          const breakdown = laneBreakdown(laneIndex, h.name);
          const fromData = hasLaneData(laneIndex, h.name);
          const laneCount = lane !== ALL ? pickCount(laneIndex, h.name, lane) : 0;
          const stat = stats(h.name, h.role);
          const openStats = scouting && clickMode === "stats";
          // The record for the team on screen, both benches, so the tooltip
          // answers "how do WE do on it" and "how do THEY" in one read.
          const statLine = stat
            ? [
                stat.ours?.games ? `ของเรา ${stat.ours.games} เกม ${stat.ours.winRate}%` : "ของเรา — ยังไม่เคยเล่น",
                stat.theirs?.games
                  ? `คู่แข่ง ${stat.theirs.games} เกม ${stat.theirs.winRate}%` +
                    (stat.theirs.watched > 0
                      ? ` (กับเรา ${stat.theirs.vsUs} · ส่อง ${stat.theirs.watched})`
                      : "")
                  : "",
              ]
                .filter(Boolean)
                .join("\n")
            : "";

          return (
            <button
              key={h.name}
              type="button"
              title={
                [
                  breakdown
                    ? `${h.name}\n${breakdown}`
                    : `${h.name}${h.role ? ` · ${h.role}` : ""}\nยังไม่เคยเล่นในไฟล์นี้ — เลนเดาจากคลาส`,
                  statLine,
                  scouting ? (openStats ? "กด = ดูสถิติ" : "กด = วางลงแมพ") : "",
                ]
                  .filter(Boolean)
                  .join("\n")
              }
              onClick={() =>
                openStats
                  ? onOpenHero(stat?.logName || h.name, stat?.logName ? stat.lane : "")
                  : onPickHero(h.name)
              }
              style={{
                appearance: "none",
                padding: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                display: "grid",
                gap: 4,
                fontFamily: T.font,
                minWidth: 0,
              }}
            >
              {/* The square lives on the <img> itself — an aspect ratio set on
                  the button collapses when the grid row sizes to content. */}
              <span
                style={{
                  position: "relative",
                  display: "block",
                  borderRadius: 8,
                  overflow: "hidden",
                  border: `1px solid ${used ? T.accent : "rgba(255,255,255,0.12)"}`,
                  background: "#0b0d12",
                }}
              >
                <img
                  src={getHeroImageUrlWithFallback(h.name)}
                  alt={h.name}
                  loading="lazy"
                  style={{
                    display: "block",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    objectFit: "cover",
                    opacity: used ? 0.5 : 1,
                  }}
                />
                {used && (
                  <span
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,16,38,0.28)",
                      display: "grid",
                      placeItems: "center",
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </span>
                )}
                {stat?.ours?.games > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      left: 2,
                      bottom: 2,
                      background: "rgba(0,0,0,0.78)",
                      color:
                        stat.ours.games < 3
                          ? T.dim
                          : stat.ours.winRate >= 50
                          ? T.green
                          : T.red,
                      borderRadius: 4,
                      padding: "0 3px",
                      fontSize: 9.5,
                      fontWeight: 600,
                      lineHeight: 1.5,
                    }}
                  >
                    {Math.round(stat.ours.winRate)}%
                  </span>
                )}
                {laneCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      right: 2,
                      bottom: 2,
                      background: "rgba(0,0,0,0.78)",
                      color: "#ffb1bb",
                      borderRadius: 4,
                      padding: "0 4px",
                      fontSize: 9.5,
                      fontWeight: 600,
                      lineHeight: 1.5,
                    }}
                  >
                    ×{laneCount}
                  </span>
                )}
                {/* A hollow dot marks a hero the workbook has never seen, so a
                    class-guessed lane is never mistaken for a real read. */}
                {hasData && !fromData && (
                  <span
                    style={{
                      position: "absolute",
                      left: 3,
                      top: 3,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      border: "1px solid rgba(255,255,255,0.65)",
                    }}
                  />
                )}
              </span>
              <span
                style={{
                  fontSize: 10,
                  lineHeight: 1.2,
                  color: used ? T.accent : T.dim,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {h.name}
              </span>
            </button>
          );
        })}
        {!results.length && (
          <div style={{ gridColumn: "1 / -1", color: T.faint, fontSize: 12, padding: "12px 0" }}>
            ไม่พบฮีโร่ที่ค้นหา
          </div>
        )}
      </div>
    </Panel>
  );
}
